import { useMemo } from "react";
import { useTrucksQuery } from "~/shared/api/govDash/govDash.api";

export interface TruckProviderGroup {
  providerId: string | number | bigint;
  providerName: string;
  truckCount: number;
  color: string;
  percentage: number;
}

export interface UseTrucksByProviderParams {
  districtOrganizationIds: (string | number | bigint)[];
  serviceProviderIds: (string | number | bigint)[];
}

/**
 * Provider colors from design (in order)
 */
const PROVIDER_COLORS = [
  "#3cb44b", // Green
  "#e6194b", // Red
  "#4363d8", // Blue
  "#ffe119", // Yellow
  "#f58231", // Orange
  "#911eb4", // Purple
  "#46f0f0", // Cyan
  "#f032e6", // Magenta
  "#bcf60c", // Lime
  "#fabebe", // Pink
  "#008080", // Teal
  "#e6beff", // Lavender
  "#9a6324", // Brown
  "#1f77b4", // Steel Blue
  "#800000", // Maroon
  "#00ff00", // Neon Green
  "#aaffc3", // Mint
  "#808000", // Olive
  "#000000", // Black
  "#000075", // Navy
  "#808080", // Gray
  "#17becf", // Turquoise
  "#9467bd", // Violet
  "#145214", // Deep Forest Green
  "#b8860b", // Dark Goldenrod
];

/**
 * Groups trucks by service provider and calculates statistics
 * Returns array sorted by truck count (descending)
 */
export const useTrucksByProvider = (params: UseTrucksByProviderParams): TruckProviderGroup[] => {
  const districtOrganizationIdsBigInt = useMemo(
    () => params.districtOrganizationIds.map((id) => BigInt(id)),
    [params.districtOrganizationIds.join(",")],
  );
  const serviceProviderIdsBigInt = useMemo(
    () => params.serviceProviderIds.map((id) => BigInt(id)),
    [params.serviceProviderIds.join(",")],
  );

  const { data: trucksResp } = useTrucksQuery({
    districtOrganizationIds: districtOrganizationIdsBigInt,
    serviceProviderIds: serviceProviderIdsBigInt,
  });

  return useMemo(() => {
    const trucks = trucksResp?.trucks || [];

    // Group trucks by service provider
    const groupMap = new Map<
      string | number | bigint,
      {
        providerId: string | number | bigint;
        providerName: string;
        count: number;
      }
    >();

    for (const truck of trucks) {
      const providerId = truck.serviceProvider?.id ?? "unknown";
      const providerName = truck.serviceProvider?.name ?? "Unknown Provider";

      if (!truck.lat || !truck.lon) {
        continue; // Skip trucks without valid location
      }

      if (!groupMap.has(providerId)) {
        groupMap.set(providerId, {
          providerId,
          providerName,
          count: 0,
        });
      }

      const group = groupMap.get(providerId)!;
      group.count += 1;
    }

    // Convert to array and sort by count descending
    const groups = Array.from(groupMap.values()).sort((a, b) => b.count - a.count);

    // Calculate total and assign colors
    const totalTrucks = groups.reduce((sum, g) => sum + g.count, 0);

    return groups
      .sort((a, b) => b.count - a.count)
      .map((group, index) => ({
        providerId: group.providerId,
        providerName: group.providerName,
        truckCount: group.count,
        color: PROVIDER_COLORS[index % PROVIDER_COLORS.length],
        percentage: totalTrucks > 0 ? Number(((group.count / totalTrucks) * 100).toFixed(1)) : 0,
      }));
  }, [trucksResp]);
};

/**
 * Get provider color by provider ID or name
 * Returns the color if provider found in groups, otherwise returns a default color
 */
export const getProviderColor = (
  groups: TruckProviderGroup[],
  providerId: string | number | bigint | undefined,
): string => {
  if (!providerId) return "#303338F2"; // Dark gray default

  const providerIdStr = String(providerId);
  const found = groups.find((g) => String(g.providerId) === providerIdStr);
  return found?.color ?? "#303338F2";
};
